import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Download,
  Settings,
  X,
  Loader,
} from "lucide-react";
import axios from "../config/axios";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import Paywall from "../components/Paywall";

/**
 * Subscription Management Page
 * Handles subscription status, billing, and plan management
 */
const Subscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [moneyBackGuarantee, setMoneyBackGuarantee] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    try {
      const token = await user.getIdToken();
      const response = await axios.get("/api/subscription/status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSubscription(response.data.subscription);
      setUsage(response.data.usage);
      setMoneyBackGuarantee(response.data.moneyBackGuarantee);

      // Show paywall if user is on free plan
      if (response.data.subscription.plan.slug === "free") {
        setShowPaywall(true);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    // Check if within 14-day guarantee period
    const startDate = new Date(subscription?.currentPeriodStart);
    const guaranteeEndDate = new Date(startDate);
    guaranteeEndDate.setDate(startDate.getDate() + 14);
    const now = new Date();
    const isWithinGuarantee = now <= guaranteeEndDate;

    let confirmMessage = "Are you sure you want to cancel your subscription?";

    if (isWithinGuarantee && !moneyBackGuarantee?.used) {
      confirmMessage = `You are within the 14-day money-back guarantee period. If you cancel now, you will receive a full refund of $${subscription?.plan.price}. Continue with cancellation?`;
    } else if (isWithinGuarantee && moneyBackGuarantee?.used) {
      confirmMessage =
        "You have already used your one-time money-back guarantee. Your subscription will be canceled at the end of the billing period. Continue?";
    } else {
      confirmMessage =
        "Are you sure you want to cancel your subscription? You will lose access to features at the end of your billing period.";
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancelLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        "/api/subscription/cancel",
        { immediately: false },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success(response.data.message);
      fetchSubscriptionData(); // Refresh data
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error("Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setCancelLoading(true);
    try {
      const token = await user.getIdToken();
      await axios.post(
        "/api/subscription/reactivate",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Subscription reactivated successfully");
      fetchSubscriptionData(); // Refresh data
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast.error("Failed to reactivate subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status, planName = null) => {
    const badges = {
      active: {
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
        text: "Active",
      },
      trialing: {
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
        text: "Active",
      },
      canceled: { color: "bg-red-100 text-red-800", icon: X, text: "Canceled" },
      past_due: {
        color: "bg-yellow-100 text-yellow-800",
        icon: AlertCircle,
        text: "Past Due",
      },
      free: { color: "bg-gray-100 text-gray-800", icon: null, text: "Free" },
    };

    const badge = badges[status] || badges.free;
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}
      >
        {badge.icon && <badge.icon className="w-4 h-4 mr-1" />}
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (showPaywall) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        <Paywall
          onClose={() => setShowPaywall(false)}
          currentPlan={subscription?.plan.name || subscription?.plan.slug}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">Subscription & Billing</h1>

      {/* Current Plan */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-semibold mb-2">Current Plan</h2>
            <div className="flex items-center space-x-4">
              <span className="text-2xl font-bold">
                {subscription?.plan.name}
              </span>
              {getStatusBadge(subscription?.status, subscription?.plan.name)}
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">
              ${(subscription?.plan.price || 0).toFixed(2)}
              <span className="text-gray-500 text-base font-normal">
                /{subscription?.plan.interval}
              </span>
            </p>
          </div>
        </div>

        {/* Money-Back Guarantee Info */}
        {subscription?.status === "active" &&
          subscription?.currentPeriodStart &&
          (() => {
            const startDate = new Date(subscription.currentPeriodStart);
            const guaranteeEndDate = new Date(startDate);
            guaranteeEndDate.setDate(startDate.getDate() + 14);
            const now = new Date();
            const isWithinGuarantee = now <= guaranteeEndDate;

            return isWithinGuarantee ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <p className="text-green-800">
                    14-Day Money-Back Guarantee valid until{" "}
                    <strong>{formatDate(guaranteeEndDate)}</strong>
                  </p>
                </div>
              </div>
            ) : null;
          })()}

        {/* Cancellation Notice */}
        {subscription?.cancelAtPeriodEnd && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                <p className="text-yellow-800">
                  Your subscription will end on{" "}
                  <strong>{formatDate(subscription.currentPeriodEnd)}</strong>
                </p>
              </div>
              <button
                onClick={handleReactivateSubscription}
                disabled={cancelLoading}
                className="text-yellow-700 hover:text-yellow-900 font-medium"
              >
                Reactivate
              </button>
            </div>
          </div>
        )}

        {/* Billing Period */}
        {subscription?.status === "active" && (
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <p className="text-gray-500">Current Period</p>
              <p className="font-medium">
                {formatDate(subscription.currentPeriodStart)} -{" "}
                {formatDate(subscription.currentPeriodEnd)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Next Payment</p>
              <p className="font-medium">
                {formatDate(subscription.currentPeriodEnd)}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {(subscription?.status === "active" ||
            subscription?.status === "trialing" ||
            subscription?.cancelAtPeriodEnd) && (
            <>
              <button
                onClick={() => setShowPaywall(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                Change Plan
              </button>

              {!subscription?.cancelAtPeriodEnd && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                  className="flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {cancelLoading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    "Cancel Subscription"
                  )}
                </button>
              )}
            </>
          )}

          {(subscription?.status === "free" ||
            subscription?.plan.slug === "free") && (
            <button
              onClick={() => setShowPaywall(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              Upgrade Plan
            </button>
          )}
        </div>
      </div>

      {/* Usage Statistics */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Usage This Month</h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">
              {usage?.analyses.thisMonth || 0}
            </p>
            <p className="text-sm text-gray-600">CRO Analyses</p>
            {usage?.analyses.limit && (
              <p className="text-xs text-gray-500 mt-1">
                of {usage.analyses.limit}
              </p>
            )}
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">
              {usage?.analyses.total || 0}
            </p>
            <p className="text-sm text-gray-600">Total Analyses</p>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">
              {usage?.features?.length || 0}
            </p>
            <p className="text-sm text-gray-600">Features Used</p>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-orange-600">
              {subscription?.plan.limits?.team_members || 1}
            </p>
            <p className="text-sm text-gray-600">Team Members</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Plan Features</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(subscription?.plan.features || {}).map(
            ([key, value]) => {
              const featureNames = {
                cro_analysis: "CRO Analysis",
                basic_recommendations: "Basic Recommendations",
                advanced_recommendations: "Advanced Recommendations",
                performance_metrics: "Performance Metrics",
                export_pdf: "Export to PDF",
                export_csv: "Export to CSV",
                competitor_analysis: "Competitor Analysis",
                conversion_funnel: "Conversion Funnel Analysis",
                ab_testing_suggestions: "A/B Testing Suggestions",
                technical_seo_audit: "Technical SEO Audit",
                email_alerts: "Email Alerts",
                priority_support: "Priority Support",
                api_access: "API Access",
                historical_tracking: "Historical Tracking",
                custom_branding: "Custom Report Branding",
                team_collaboration: "Team Collaboration",
              };

              const name =
                featureNames[key] ||
                key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

              return (
                <div key={key} className="flex items-center">
                  {value ? (
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  ) : (
                    <X className="w-5 h-5 text-gray-300 mr-2" />
                  )}
                  <span className={value ? "text-gray-700" : "text-gray-400"}>
                    {name}
                  </span>
                </div>
              );
            },
          )}
        </div>

        {subscription?.plan.slug !== "pro" &&
          subscription?.plan.slug !== "enterprise" && (
            <div className="mt-6 pt-6 border-t">
              <button
                onClick={() => setShowPaywall(true)}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                Unlock All Features with Pro
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default Subscription;
